import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Input, Segmented, Space, Table, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { FctCard, FctListHeader, fctColors } from '../estilo-corporativo-fct';
import {
  CasoAltaResumen,
  EstadoCaso,
  OrigenCaso,
  interpretarError,
  listarCasos,
} from '../api/altasCasosApi';
import { EstadoCasoTag, OrigenTag, EmailVerificadoBadge } from '../components/Altas/casos/tags';
import { ESTADO_LABELS, ORIGEN_LABELS, formatearRef } from '../components/Altas/casos/estado';

type Pestania = 'por_tratar' | 'tratados';

/**
 * Pantalla 1 — Listado de gestión de altas (F1.4, spec §5; GESCL-T01 tramo 5).
 *
 * Patrón de pestañas COPIADO CON REVISIÓN del mockup T05 (`ListadoGestionPage`, ajuste 1
 * firme): conmutador `Segmented` de 2 opciones junto al título (no Tabs), **sin contadores en
 * las pestañas** (evita el conteo caro de "Tratados" al cargar). En la revisión, los Badge
 * contadores de los filtros internos del mockup se retiran también: aquí el filtrado es de
 * servidor (paginado) y calcularlos costaría una query por estado; el total filtrado ya lo da
 * la propia tabla. Sin botón "Restablecer" (ajuste 2): los controles cubren el gesto.
 */
const AltasGestionPage: React.FC = () => {
  const navigate = useNavigate();
  const [pestania, setPestania] = useState<Pestania>('por_tratar');
  // 0 = "Todos" (centinela numérico: Segmented de AntD 5.1 tipa mal las uniones string|enum)
  const [filtroEstado, setFiltroEstado] = useState<number>(0);
  const [filtroOrigen, setFiltroOrigen] = useState<number>(0);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanio, setTamanio] = useState(20);
  const [cargando, setCargando] = useState(false);
  const [casos, setCasos] = useState<CasoAltaResumen[]>([]);
  const [total, setTotal] = useState(0);
  // Contadores por estado para los badges de "Por tratar" (solo llegan poblados en esa pestaña).
  const [contadores, setContadores] = useState<Record<number, number>>({});

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await listarCasos({
        tratados: pestania === 'tratados',
        estado: filtroEstado === 0 ? undefined : (filtroEstado as EstadoCaso),
        origen: filtroOrigen === 0 ? undefined : (filtroOrigen as OrigenCaso),
        q: busquedaAplicada || undefined,
        pagina,
        tamanio,
      });
      setCasos(res.casos);
      setTotal(res.totalFiltrado);
      setContadores(res.contadoresPorEstado ?? {});
    } catch (error) {
      message.error(interpretarError(error).mensaje);
    } finally {
      setCargando(false);
    }
  }, [pestania, filtroEstado, filtroOrigen, busquedaAplicada, pagina, tamanio]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiarPestania = (p: Pestania) => {
    setPestania(p);
    setFiltroEstado(0);
    setPagina(1);
  };

  const opcionesFiltroEstado = useMemo(() => {
    // "Por tratar": filtros CON badge de conteo (mockup validado, ajuste 1); el listado es
    // siempre corto (5-20 registros), el conteo es barato. "Tratados": SIN badge (el listado
    // crece sin límite y el conteo sería caro — decisión del Dev 2026-09-10).
    if (pestania === 'por_tratar') {
      const pend = contadores[EstadoCaso.Pendiente] ?? 0;
      const esp = contadores[EstadoCaso.EnEspera] ?? 0;
      const conBadge = (label: string, value: number, count: number, colorHex: string) => ({
        value,
        label: (
          <Space size={8} align="center">
            <span>{label}</span>
            <Badge count={count} style={{ backgroundColor: colorHex }} showZero overflowCount={9999} />
          </Space>
        ),
      });
      return [
        // "Todos" = Pendiente + En espera combinados (estado visible como tag — resuelve el apaño de GDeco §3.1).
        conBadge('Todos', 0, pend + esp, fctColors.verdeCorporativo),
        conBadge('Pendiente', EstadoCaso.Pendiente as number, pend, fctColors.estadoPendiente),
        conBadge('En espera', EstadoCaso.EnEspera as number, esp, '#d4b106' /* gold-7 AntD, igual que el mockup */),
      ];
    }
    const estados: EstadoCaso[] = [
      EstadoCaso.RegistroValidado,
      EstadoCaso.ClienteExistenteActualizado,
      EstadoCaso.RegistroEliminado,
      EstadoCaso.AltaDirecta,
    ];
    const opciones = estados.map((e) => ({
      label: e === EstadoCaso.RegistroEliminado ? 'Alta descartada' : ESTADO_LABELS[e],
      value: e as number,
    }));
    return [...opciones, { label: 'Todos', value: 0 }];
  }, [pestania, contadores]);

  const columns: ColumnsType<CasoAltaResumen> = [
    {
      title: 'Fecha',
      dataIndex: 'fechaAlta',
      key: 'fecha',
      width: 140,
      render: (v: string) => dayjs(v).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Ref',
      key: 'ref',
      width: 140,
      render: (_, caso) => (
        <span>
          {formatearRef(caso.id)}
          {caso.idCodigoGf && (
            <>
              <br />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {caso.idCodigoGf}
              </Typography.Text>
            </>
          )}
        </span>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 190,
      render: (estado: EstadoCaso) => <EstadoCasoTag estado={estado} />,
    },
    {
      title: 'Origen',
      dataIndex: 'origen',
      key: 'origen',
      width: 100,
      render: (origen: OrigenCaso) => <OrigenTag origen={origen} />,
    },
    {
      title: 'Nombre / Apellidos',
      key: 'nombre',
      render: (_, caso) =>
        `${caso.nombre ?? ''} ${caso.apellido1 ?? ''} ${caso.apellido2 ?? ''}`.trim() || (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, caso) =>
        caso.email ? (
          <span>
            {caso.email}{' '}
            {/* Indicador ✓/✗ como en el mockup validado: para toda fila web con email se muestra
                siempre (null "sin comprobar" cae a no verificado ✗); el matiz "sin comprobar"
                queda en la cabecera del tratamiento. */}
            <EmailVerificadoBadge verificado={caso.emailVerificado ?? false} aplica={caso.origen === OrigenCaso.Web} />
          </span>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Notas de gestión',
      key: 'notas',
      width: 260,
      render: (_, caso) => {
        if (!caso.ultimaNota) return <Typography.Text type="secondary">—</Typography.Text>;
        return (
          <Tooltip
            title={
              caso.fechaUltimaNota
                ? `${dayjs(caso.fechaUltimaNota).format('DD/MM HH:mm')} · ${caso.ultimaNota}`
                : caso.ultimaNota
            }
          >
            <span
              style={{
                display: 'inline-block',
                maxWidth: 240,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {caso.ultimaNota}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <FctCard
      title={
        <Space size={16} align="center">
          <span>Altas de clientes</span>
          <Segmented
            value={pestania}
            onChange={(v) => cambiarPestania(v as Pestania)}
            options={[
              { label: 'Por tratar', value: 'por_tratar' },
              { label: 'Tratados', value: 'tratados' },
            ]}
          />
        </Space>
      }
      // Botón "Clientes" (acceso al grid F1.2, spec §5.1) — presente como en el mockup validado;
      // el grid de clientes se construye en GESCL-T04, así que de momento solo lo anuncia.
      extra={
        <Tooltip title="El grid de clientes llega con GESCL-T04">
          <Button icon={<TeamOutlined />} onClick={() => message.info('El grid de clientes estará disponible en una próxima entrega (GESCL-T04).')}>
            Clientes
          </Button>
        </Tooltip>
      }
    >
      <FctListHeader
        leftContent={
          <Segmented
            size="large"
            value={filtroEstado}
            onChange={(v) => {
              setFiltroEstado(v as number);
              setPagina(1);
            }}
            options={opcionesFiltroEstado}
          />
        }
      >
        <Segmented
          value={filtroOrigen}
          onChange={(v) => {
            setFiltroOrigen(v as number);
            setPagina(1);
          }}
          options={[
            { label: 'Todos', value: 0 },
            { label: ORIGEN_LABELS[OrigenCaso.Web], value: OrigenCaso.Web as number },
            { label: ORIGEN_LABELS[OrigenCaso.Manual], value: OrigenCaso.Manual as number },
            { label: ORIGEN_LABELS[OrigenCaso.Asesor], value: OrigenCaso.Asesor as number },
          ]}
        />
        <Input.Search
          placeholder="Buscar por nombre, apellidos o email..."
          prefix={<SearchOutlined />}
          style={{ width: 300 }}
          allowClear
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          onSearch={(v) => {
            setBusquedaAplicada(v.trim());
            setPagina(1);
          }}
        />
      </FctListHeader>

      <Table<CasoAltaResumen>
        rowKey="id"
        size="middle"
        loading={cargando}
        columns={columns}
        dataSource={casos}
        pagination={{
          current: pagina,
          pageSize: tamanio,
          total,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (t) => `${t} casos`,
          onChange: (p, ps) => {
            setPagina(p);
            setTamanio(ps);
          },
        }}
        onRow={(caso) => ({
          style: { cursor: 'pointer' },
          // Caso final → la pantalla de tratamiento abre en solo lectura (spec §5.1).
          onClick: () => navigate(`/altas/${caso.id}`),
        })}
      />
    </FctCard>
  );
};

export default AltasGestionPage;
